import sentry from '../../sentry';
import {
  useMemo,
  useContext,
  useRef,
  useCallback,
  useState,
  type Context,
} from 'react';
import { useDisclosure, useToast } from '@chakra-ui/react';

import MessagingHistoryList from '../../components/client/messages/MessagingHistoryList';
import MessagingHistoryContext from '../../store/client/MessagingHistoryContext';

import CustomToast from '../../common/CustomToast';
import { HttpContext } from '../../context/HttpContext';

import moment from 'moment-timezone';
import AlertDialogWrapper from '../../components/common/AlertDialogWrapper';
import { useReportAndToastApiError } from '../../hooks/useReportAndToastApiError';
import { getMessagingHistoryColumns } from '../../components/client/messages/messagingHistoryTableColumns';
import type {
  MessagingHistoryData,
  MessagingHistoryFetchParams,
  BatchesResponse,
  CancelOptions,
  CancelResponse,
  HttpContextValue,
  MessagingHistoryContextValue,
  MessagingPageProps,
} from '../../components/client/messages/messagingHistory.types';

const localTimezone = moment.tz.guess();

const formatSendTime = (value?: string | null): string | null => {
  if (!value) return null;

  const timestamp = moment(value);
  return timestamp.isValid()
    ? timestamp.tz(localTimezone).format('D MMM YYYY, HH:mm z')
    : null;
};

const MessageHistoryView = ({
  filterEntity,
  status = '',
  heading = 'Message Batches',
}: MessagingPageProps) => {
  const { authAxios } = useContext(HttpContext as Context<HttpContextValue>);
  const toast = useToast();
  const reportAndToastApiError = useReportAndToastApiError();
  const fetchIdRef = useRef(0);
  const { dataState, updateDataState } = useContext(
    MessagingHistoryContext as Context<MessagingHistoryContextValue>
  );
  const [cancelOptions, setCancelOptions] = useState<CancelOptions>({
    id: null,
    messageType: null,
  });
  const { isOpen, onOpen, onClose } = useDisclosure();

  const columns = useMemo(() => getMessagingHistoryColumns(status), [status]);

  const fetchData = useCallback(
    ({ pageSize, pageIndex }: MessagingHistoryFetchParams): void => {
      const fetchId = ++fetchIdRef.current;
      const safePageSize = pageSize > 0 ? pageSize : 10;

      updateDataState({
        loading: true,
        status: 'pending',
      });

      setTimeout(async () => {
        if (fetchId === fetchIdRef.current) {
          try {
            const statusQuery = status ? `&status=${status}` : '';
            const typeQuery = ['walletMessaging', 'MessageBatch']
              .map((type) => `type[]=${type}`)
              .join('&');
            const response = await authAxios.get<BatchesResponse>(
              `/api/v1/entities/${filterEntity.uuid}/batches?per_page=${safePageSize}&page=${pageIndex + 1}${statusQuery}&${typeQuery}`
            );
            const batches = response.data?.data ?? [];
            const total = response.data?.meta?.totalCount ?? 0;

            updateDataState({
              items: batches,
              total,
              pageCount: Math.ceil(total / safePageSize),
              loading: false,
              status: 'resolved',
            });
          } catch (error) {
            sentry.sendError(error);
            updateDataState({
              items: [],
              total: 0,
              pageCount: 0,
              loading: false,
              status: 'rejected',
            });
          }
        }
      }, 0);
    },
    [authAxios, filterEntity, status]
  );

  const handleCancel = useCallback(
    (id: string | number, messageType: string): void => {
      setCancelOptions({ id, messageType });
      onOpen();
    },
    [onOpen]
  );

  const handleConfirmCancel = useCallback((): void => {
    let url: string;
    if (cancelOptions.messageType === 'Wallet') {
      url = `/api/v1/batches/${cancelOptions.id}/cancel`;
    } else {
      url = `/api/v1/messaging/history/${cancelOptions.id}/cancel`;
    }
    authAxios
      .patch<CancelResponse>(url)
      .then((response) => {
        toast({
          render: (props) => (
            <CustomToast
              status="success"
              title={response.data.message}
              description=""
              onClose={props.onClose}
            />
          ),
        });
        fetchData({ pageSize: 10, pageIndex: 0 });
      })
      .catch((error: unknown) => {
        reportAndToastApiError(error);
      })
      .finally(() => {
        onClose();
        setCancelOptions({ id: null, messageType: null });
      });
  }, [
    authAxios,
    cancelOptions,
    fetchData,
    onClose,
    reportAndToastApiError,
    toast,
  ]);

  const normalizedData = useMemo<MessagingHistoryData[]>(() => {
    if (dataState.items.length === 0) return [];

    return dataState.items.map(({ id, type, attributes }) => ({
      id,
      ...attributes,
      messageType:
        type === 'walletMessaging' ? 'Wallet' : (attributes.messageType ?? ''),
      scheduledAt: attributes.scheduledAt
        ? moment(attributes.scheduledAt).format('MMM D, YYYY HH:mm')
        : null,
      scheduledAtRaw: attributes.scheduledAt,
      sendTime: formatSendTime(attributes.scheduledAt || attributes.createdAt),
      completedAt: attributes.completedAt
        ? moment(attributes.completedAt).format('MMM D, YYYY HH:mm')
        : null,
      recipients: attributes.totalRecipients,
      errors: attributes.errorCount,
      errorsCsv: attributes.errorsCsvUrl,
      deliveryReportCsv: attributes.deliveryReportCsvUrl,
    }));
  }, [dataState.items]);

  return (
    <>
      <MessagingHistoryList
        columns={columns}
        heading={heading}
        cancel={handleCancel}
        data={normalizedData}
        fetchData={fetchData}
        isLoading={dataState.loading}
        pageCount={dataState.pageCount}
        total={dataState.total}
        tableType={status}
      />
      <AlertDialogWrapper
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setCancelOptions({ id: null, messageType: null });
        }}
        onConfirm={handleConfirmCancel}
        title="Cancel Message Batch"
        body="Are you sure you want to cancel this message batch? You won't be able to undo this action."
        buttonText="Cancel Batch"
      />
    </>
  );
};

export default MessageHistoryView;
